import { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from './useAuth';

interface SessionTimeoutConfig {
  timeoutMinutes?: number;
  warningMinutes?: number;
  onWarning?: () => void;
  onTimeout?: () => void;
}

export const useSessionTimeout = ({
  timeoutMinutes = 30,
  warningMinutes = 5,
  onWarning,
  onTimeout
}: SessionTimeoutConfig = {}) => {
  const { user, signOut } = useAuth();
  const [isWarningShown, setIsWarningShown] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningMinutes * 60 * 1000;

  // Função para resetar os timers
  const resetTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
    }
    
    setIsWarningShown(false);
    lastActivityRef.current = Date.now();

    if (!user) return;

    // Timer para mostrar aviso
    warningTimeoutRef.current = setTimeout(() => {
      setIsWarningShown(true);
      setRemainingTime(warningMs);
      onWarning?.();

      // Countdown para o aviso
      countdownRef.current = setInterval(() => {
        const elapsed = Date.now() - lastActivityRef.current;
        const remaining = timeoutMs - elapsed;
        
        if (remaining <= 0) {
          clearInterval(countdownRef.current!);
          handleTimeout();
        } else {
          setRemainingTime(remaining);
        }
      }, 1000);
    }, timeoutMs - warningMs);

    // Timer para logout automático
    timeoutRef.current = setTimeout(() => {
      handleTimeout();
    }, timeoutMs);
  }, [user, timeoutMs, warningMs, onWarning]);

  // Função para lidar com timeout
  const handleTimeout = useCallback(async () => {
    setIsWarningShown(false);
    onTimeout?.();
    await signOut();
  }, [onTimeout, signOut]);

  // Função para estender sessão
  const extendSession = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  // Refs para funções estáveis
  const resetTimersRef = useRef(resetTimers);
  const handleTimeoutRef = useRef(handleTimeout);

  // Atualizar refs quando funções mudarem
  useEffect(() => {
    resetTimersRef.current = resetTimers;
    handleTimeoutRef.current = handleTimeout;
  }, [resetTimers, handleTimeout]);

  // Eventos que indicam atividade do usuário
  const handleUserActivity = useCallback(() => {
    if (user) {
      resetTimersRef.current();
    }
  }, [user]);

  // Configurar listeners de atividade
  useEffect(() => {
    if (!user) return;

    const events = [
      'mousedown',
      'keypress',
      'scroll',
      'touchstart',
      'click'
    ];

    // Throttle para evitar muitos resets
    let throttleTimer: NodeJS.Timeout | null = null;
    const throttledActivity = () => {
      if (throttleTimer) return;
      
      throttleTimer = setTimeout(() => {
        if (user) {
          resetTimersRef.current();
        }
        throttleTimer = null;
      }, 30000); // Throttle de 30 segundos
    };

    events.forEach(event => {
      document.addEventListener(event, throttledActivity, true);
    });

    // Inicializar timers
    resetTimersRef.current();

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledActivity, true);
      });
      
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (throttleTimer) clearTimeout(throttleTimer);
    };
  }, [user, timeoutMinutes, warningMinutes]);

  // Limpar timers quando usuário faz logout
  useEffect(() => {
    if (!user) {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningTimeoutRef.current) clearTimeout(warningTimeoutRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      setIsWarningShown(false);
    }
  }, [user]);

  return {
    isWarningShown,
    remainingTime,
    extendSession,
    formatRemainingTime: () => {
      const minutes = Math.floor(remainingTime / 60000);
      const seconds = Math.floor((remainingTime % 60000) / 1000);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  };
};
